/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import cx from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';

import { getLogoTitle } from '../../helpers/serviceDeskHelpers';
import { AMAZON_CONNECT_BYOSD_TYPE } from '../../util/channelTypes';
import './AmazonConnect.scss';

interface AmazonConnectIconProps {
  className?: string;
  size?: number;
}

export const AmazonConnectIcon = ({
  className,
  size = 32,
  ...others
}: AmazonConnectIconProps) => {
  const intl = useIntl();
  const pxSize = size + 'px';
  return (
    <svg
      className={cx('AmazonConnectIcon', className)}
      height={pxSize}
      id="AmazonConnect_Layer_1"
      data-name="Layer 1"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 460.35 158.65"
      {...others}>
      <title>{getLogoTitle(AMAZON_CONNECT_BYOSD_TYPE, intl)}</title>
      <path
        className="amazon__a"
        d="M236,63.25l-3.36-9.81H216.5l-3.2,9.81H202.58l16.65-43h11.25l16.65,43Zm-17.32-16.5h11.86l-6-18Z"
        transform="translate(0.02 0)"
      />
      <path
        className="amazon__a"
        d="M294.7,30.65c3.18,0,5.64.8,7.4,2.4a8.72,8.72,0,0,1,2.63,6.77V63.25h-10V41.79c0-2.81-1.39-4.22-4.16-4.21a18.65,18.65,0,0,0-7.43,1.6V63.25h-10V41.79c0-2.81-1.38-4.21-4.16-4.21a18.28,18.28,0,0,0-7.53,1.66v24H251.5V31.55h8.19l.9,3.06a30.26,30.26,0,0,1,6.51-3.07,20,20,0,0,1,6-.89q6.12,0,8.66,4A24.52,24.52,0,0,1,294.7,30.65Z"
        transform="translate(0.02 0)"
      />
      <path
        className="amazon__a"
        d="M322.89,64.25a13,13,0,0,1-8.45-2.6,8.57,8.57,0,0,1-3.17-7q0-6.07,6.66-8.78a18.5,18.5,0,0,1,6.93-1.3,38.67,38.67,0,0,1,7.66.79V42.3c0-1.9-.43-3.2-1.28-3.9s-2.79-1.06-5.82-1.06a46.8,46.8,0,0,0-11.63,1.9v-6a27.77,27.77,0,0,1,6.51-1.89,43.07,43.07,0,0,1,7.93-.71q7.41,0,10.82,2.57c2.28,1.71,3.42,4.41,3.42,8.08v22H334l-.86-2.79a16.77,16.77,0,0,1-4.89,2.76A15.33,15.33,0,0,1,322.89,64.25ZM325.68,58a11.38,11.38,0,0,0,6.84-2.49V50.22a41.61,41.61,0,0,0-5.35-.41q-5.91,0-5.94,4.23a3.69,3.69,0,0,0,1.18,3A4.76,4.76,0,0,0,325.68,58Z"
        transform="translate(0.02 0)"
      />
      <path
        className="amazon__a"
        d="M347.63,63.25V56.8l17.91-18.56H348.29V31.55h28.24V38L358.38,56.56h18.48v6.69Z"
        transform="translate(0.02 0)"
      />
      <path
        className="amazon__a"
        d="M410.68,59.77q-4.56,4.41-12.67,4.42t-12.69-4.42q-4.57-4.43-4.58-12.34t4.58-12.35q4.57-4.44,12.69-4.43t12.67,4.43q4.56,4.44,4.56,12.35T410.68,59.77ZM398,57.26q7.17,0,7.16-9.86T398,37.58q-7.15,0-7.16,9.82T398,57.26Z"
        transform="translate(0.02 0)"
      />
      <path
        className="amazon__a"
        d="M444.7,42.48c0-3.27-1.66-4.9-5-4.9a14.75,14.75,0,0,0-7.46,2.12V63.25h-10V31.55h8.19l1,3.42a22.27,22.27,0,0,1,13.09-4.32c3.21,0,5.69.79,7.46,2.37a8.7,8.7,0,0,1,2.63,6.8V63.25h-10Z"
        transform="translate(0.02 0)"
      />
      <path
        className="amazon__a"
        d="M212.07,120.55q-5.81-5.53-5.8-16.49t6.08-16.77q6.08-5.82,17.33-5.82a38.23,38.23,0,0,1,11.39,1.68V90.3A53,53,0,0,0,230.88,89q-7.09,0-10.6,3.47t-3.52,10.6v1.45q0,7,3.42,10.49t10.44,3.45A58.72,58.72,0,0,0,241.74,117v7.17a39.56,39.56,0,0,1-12.36,1.88Q217.86,126.07,212.07,120.55Z"
        transform="translate(0.02 0)"
      />
      <path
        className="amazon__a"
        d="M276.35,121.77q-4.56,4.41-12.67,4.42T251,121.77q-4.59-4.42-4.58-12.34T251,97.08q4.58-4.42,12.68-4.43t12.67,4.43q4.57,4.44,4.57,12.35T276.35,121.77Zm-12.68-2.51q7.15,0,7.16-9.86t-7.16-9.82q-7.17,0-7.16,9.82T263.67,119.26Z"
        transform="translate(0.02 0)"
      />
      <path
        className="amazon__a"
        d="M310.37,104.48c0-3.27-1.67-4.91-5-4.9A14.86,14.86,0,0,0,298,101.7v23.55H288V93.55h8.2l1,3.42a22.27,22.27,0,0,1,13.09-4.32c3.22,0,5.7.79,7.46,2.37a8.7,8.7,0,0,1,2.63,6.8v23.43h-10Z"
        transform="translate(0.02 0)"
      />
      <path
        className="amazon__a"
        d="M351.42,104.48c0-3.27-1.67-4.91-5-4.9A14.81,14.81,0,0,0,339,101.7v23.55H329V93.55h8.19l1,3.42a22.22,22.22,0,0,1,13.08-4.32q4.83,0,7.46,2.37a8.7,8.7,0,0,1,2.63,6.8v23.43h-9.95Z"
        transform="translate(0.02 0)"
      />
      <path
        className="amazon__a"
        d="M400.05,105.66a22.46,22.46,0,0,1-.7,5.94H378.27q.27,4.09,2.71,5.94c1.63,1.22,4.08,1.84,7.34,1.84a44.92,44.92,0,0,0,11-1.55v6q-5.07,2.34-13.11,2.35t-13-4.27q-4.9-4.27-4.9-12.37T373.16,97a17.48,17.48,0,0,1,12.22-4.4q7.37,0,11,3.37T400.05,105.66Zm-14.37-6.75a7.41,7.41,0,0,0-5.27,1.82,7.78,7.78,0,0,0-2.2,5.3h13.32v-1Q391.53,98.91,385.68,98.91Z"
        transform="translate(0.02 0)"
      />
      <path
        className="amazon__a"
        d="M409.23,121.74q-4.39-4.21-4.4-12.13t4.65-12.32q4.65-4.4,12.87-4.4a21.45,21.45,0,0,1,9.22,2v6a29.08,29.08,0,0,0-7.36-1.06q-4.75,0-7,2.21c-1.51,1.48-2.27,3.8-2.27,7v.88q0,4.72,2.22,6.93a8.45,8.45,0,0,0,6.21,2.19,34.21,34.21,0,0,0,8.42-1.12v6a20.44,20.44,0,0,1-9.82,2Q413.63,126,409.23,121.74Z"
        transform="translate(0.02 0)"
      />
      <path
        className="amazon__a"
        d="M451.48,125.73q-10.73,0-10.73-10.15V100.24h-5.26V94.67l5.53-1,1.5-8.72h8.19v8.56h9.42v6.69h-9.42v14.94a3.32,3.32,0,0,0,1.1,2.82,6,6,0,0,0,3.45.8,39,39,0,0,0,5.07-.36v6A26.59,26.59,0,0,1,451.48,125.73Z"
        transform="translate(0.02 0)"
      />
      <path
        className="amazon__a"
        d="M167.32,48.37A79.09,79.09,0,0,0,94.32,0h-.16a79.83,79.83,0,0,0-41,11.8c-12,7.27-22,17.48-28.18,28.76a2,2,0,1,0,2.87,2.78,1.76,1.76,0,0,0,.27-.34,2,2,0,0,0,.32-.57c5.78-10.68,15.28-20.37,26.7-27.25A75.78,75.78,0,0,1,94.14,4h.18A75.43,75.43,0,1,1,29.16,117.69a2,2,0,0,0-3.4,2A79.44,79.44,0,0,0,167.32,48.37M9.57,56.91H34.51a2,2,0,0,0,0-3.95H9.57a2,2,0,0,0,0,3.95M26,80.53a2,2,0,0,0-2-2H2.32a2,2,0,1,0-.63,4,2.22,2.22,0,0,0,.63,0H24.06a2,2,0,0,0,2-2m8.4,25.59a2,2,0,0,0-2-2H5.78a2,2,0,0,0,0,4H32.45a2,2,0,0,0,2-2M52.05,64.6a2,2,0,0,0,1.63-2.27c-1.46-8.84,0-16.7,4.28-22.74A27,27,0,0,1,74.32,29.11c13.44-3,29.61,3.1,34.5,20.51a2,2,0,0,0,1.07,1.26,2,2,0,0,0,1.66,0c5.45-2.52,11-2.7,15.65-.5s8.16,6.69,9.67,12.53a2,2,0,0,0,1.56,1.45c12.89,2.33,18.29,12.17,17.95,20.85a19.54,19.54,0,0,1-5,12.35,21.36,21.36,0,0,1-13.86,6.55H115.8a2,2,0,0,0-1.35.53L81.32,135.51V105.88a2,2,0,0,0-2-2H52.79a19,19,0,0,1-13.12-6.27,20.33,20.33,0,0,1-5-12.59,19.53,19.53,0,0,1,3.91-12.81c3-4,7.7-6.59,13.45-7.53M30.75,85.21c.45,10.17,7.48,21.58,21.82,22.63H77.39V140a2,2,0,0,0,3.32,1.45l35.87-33.4h21.25c15-1.51,22.1-12.48,22.5-22.69a23.93,23.93,0,0,0-5-15.6,25.68,25.68,0,0,0-15-9.08c-2-6.41-6-11.32-11.47-13.91a21.55,21.55,0,0,0-16.93-.37A31.65,31.65,0,0,0,96.74,28.31a33.89,33.89,0,0,0-23.31-3A30.89,30.89,0,0,0,54.68,37.46c-4.56,6.46-6.35,14.64-5.23,23.76-13.52,3.07-19.17,14.1-18.73,24m32.6-10.45a5.68,5.68,0,1,1-5.68,5.68h0a5.68,5.68,0,0,1,5.68-5.68h0m23-17a5.68,5.68,0,1,1-5.68,5.68h0a5.7,5.7,0,0,1,5.68-5.68M105.5,90.63A5.68,5.68,0,1,1,99.77,85h0a5.7,5.7,0,0,1,5.68,5.68m16.11-23.14a5.68,5.68,0,1,1-5.67,5.69h0a5.69,5.69,0,0,1,5.67-5.68M63.32,90.08a9.63,9.63,0,0,0,8.84-13.45l7.6-6.14A9.6,9.6,0,0,0,87.87,73L93,83.86a9.63,9.63,0,1,0,15.35,2.35l7.15-5.55A9.61,9.61,0,1,0,113,77.57l-7.2,5.6a9.59,9.59,0,0,0-9.47-1.46L91.59,71.6a9.63,9.63,0,1,0-14.08-4.39L69.9,73.36a9.63,9.63,0,1,0-6.58,16.72"
        transform="translate(0.02 0)"
      />
    </svg>
  );
};
